import { Popover } from '@nextui-org/react';
import { css } from 'styled-components';
import styled from 'styled-components';

import {
  ActivityIcon,
  BellIcon,
  BookIcon,
  CarIconSearch,
  IntegrationsIcon,
  NotificationsCloseIcon,
  OrganizationIcon,
  OverviewIcon,
  QuickValuationIcon,
  VehicleConditionIcon,
  VehicleInformationIcon,
  VinCarLogoMark,
  WorkflowIcon,
  WriteNewMsgIcon,
} from '../../../../../../assets';
import { VinCarLogo } from '../../../../../../assets';
import { ArrowDownIcon } from '../../../../../../assets';
import ZIndexLayers from '../../../../../../services/ZIndexLayers';
import zIndexLayers from '../../../../../../services/ZIndexLayers/ZIndexLayers';
import { fadeAnimation } from '../../../../../../theme/GlobalStyles';
import { AppTheme, ThemeProp } from '../../../../../../theme/Themes';
import { setSVGChildColors } from '../../../../../../utils/Listings';
import Avatar, { AvatarVariant } from '../../../../../Avatar';
import Collapse from '../../../../../Collapse';
import { StyledCollapseTitle } from '../../../../../Collapse/Collapse.styles';
import { drawerTransitionDuration } from '../../../BaseDrawerLayout/BaseDrawerLayout.styles';

export const navigationItemBorderRadius = '.375rem';
const navigationItemHeight = 36;
const navigationItemMargin = 4;

interface WithDrawerIsOpen {
  $drawerIsOpen: boolean;
}

interface AppDrawerContentContainerProps extends WithDrawerIsOpen {}

const horizontalPadding = '1rem';

export const AppDrawerContentContainer = styled.div<AppDrawerContentContainerProps>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 1rem 0;
  background: ${(props) => props.theme.colors.background};
  border-right: solid 1px ${(props: ThemeProp) => props.theme.colors.primary.c800};
  width: ${(props) => (props.$drawerIsOpen ? '240px' : '77px')};
  transition: width ${drawerTransitionDuration} cubic-bezier(0.4, 0, 0.6, 1) 0ms;
  overflow: hidden;
  z-index: ${zIndexLayers.drawer};
  position: relative;
`;

interface TopContainerProps {
  $addBorderBottom?: boolean;
}

export const NavigationItemsContainer = styled.div<TopContainerProps & WithDrawerIsOpen>`
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0 ${horizontalPadding} 0.5rem ${horizontalPadding};
  border-bottom: ${(props) => (props.$addBorderBottom ? `1px solid ${props.theme.colors.greyscale.c600}` : 'none')};
  ${(props: WithDrawerIsOpen) => {
    if (!props.$drawerIsOpen) {
      return `
        overflow: hidden;
      `;
    }
    return null;
  }}
`;

export const Top = styled.div<TopContainerProps & WithDrawerIsOpen>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  ${(props: WithDrawerIsOpen) => {
    if (!props.$drawerIsOpen) {
      return `
        overflow: hidden;
      `;
    }
    return null;
  }}
`;

export const Bottom = styled.div`
  padding: 0.625rem ${horizontalPadding};
`;

interface BrandContainerProps extends WithDrawerIsOpen {}
export const BrandContainer = styled.div<BrandContainerProps>`
  align-items: center;
  justify-content: flex-start;
  display: flex;
  padding: ${({ $drawerIsOpen }: BrandContainerProps) => ($drawerIsOpen ? '0.5rem 1.5rem' : '0.5rem 1.625rem')};
  margin-bottom: 1rem;
  width: 240px;
  transition: padding ${drawerTransitionDuration};
`;

const commonDrawerSectionContainerStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const NavigationItems = styled.div`
  ${commonDrawerSectionContainerStyles};
`;

export const SystemItemsContainer = styled.div`
  ${commonDrawerSectionContainerStyles};
`;

export const MenuContainer = styled.div`
  svg {
    width: ${20 / 16}rem;
    height: ${20 / 16}rem;
  }
`;

const getCommonCollapsedIconAttributes = ({ theme, $drawerIsOpen }: ThemeProp & AppDrawerContentContainerProps) => ({
  stroke: getIconStroke(false, $drawerIsOpen, theme),
  width: `${20 / 16}rem`,
  height: `${20 / 16}rem`,
});
export const StyledCarIcon = styled(CarIconSearch).attrs(getCommonCollapsedIconAttributes)``;
export const StyledWorkflowIcon = styled(WorkflowIcon).attrs(getCommonCollapsedIconAttributes)``;
export const StyledOverviewIcon = styled(OverviewIcon).attrs(getCommonCollapsedIconAttributes)``;
export const StyledIntegrationsIcon = styled(IntegrationsIcon).attrs(getCommonCollapsedIconAttributes)``;
export const StyledOrganizationsIcon = styled(OrganizationIcon).attrs(getCommonCollapsedIconAttributes)``;
export const StyledActivityIcon = styled(ActivityIcon).attrs(getCommonCollapsedIconAttributes)``;
export const StyledHistoryIcon = styled(BookIcon).attrs(getCommonCollapsedIconAttributes)``;
export const StyledNotesIcon = styled(WriteNewMsgIcon).attrs(getCommonCollapsedIconAttributes)``;
export const StyledVehicleInformationIcon = styled(VehicleInformationIcon).attrs(getCommonCollapsedIconAttributes)``;
export const StyledVehicleConditionIcon = styled(VehicleConditionIcon).attrs(getCommonCollapsedIconAttributes)``;
export const StyledQuickValuationIcon = styled(QuickValuationIcon).attrs(getCommonCollapsedIconAttributes)``;

export const Badge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 8px;
  top: -5px;
  padding: 1px 4px;
  height: 0.875rem;
  border-radius: 40px;
  color: ${(props: ThemeProp) => props.theme.colors.black};
  background-color: ${(props: ThemeProp) => props.theme.colors.main.c500};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xxs};
`;

interface LogoProps {
  $drawerIsOpen?: boolean;
}

export const Logo = styled(VinCarLogo)<LogoProps>`
  #logo-text {
    opacity: ${(props: LogoProps) => (props.$drawerIsOpen ? '1' : '0')};
    transition: opacity ${drawerTransitionDuration};
  }
`;

export const LogoMark = styled(VinCarLogoMark)``;

export const NotificationIconContainer = styled.div`
  display: flex;
  position: relative;
`;

export const StyledBellIcon = styled(BellIcon).attrs(getCommonCollapsedIconAttributes)`
  width: 1.25rem;
  height: 1.25rem;
`;

const ContentContainer = styled.div`
  display: flex;
  height: 100%;
  vertical-align: middle;
  align-self: flex-start;
`;

export const CollapsedAvatarContainer = styled.div`
  position: relative;
  margin-top: 0.5rem;
  max-width: 2rem;

  margin-left: auto;
  margin-right: auto;
  &:hover {
    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background: ${({ theme }: ThemeProp) => theme.colors.secondary['12-light-blue']};
      border-radius: ${navigationItemBorderRadius};
    }
  }
`;

interface AnimationProps {
  $animated?: boolean;
}

export const AnimationContainer = styled.div<AnimationProps>`
  ${(props: AnimationProps) =>
    props.$animated &&
    css`
      animation: ${fadeAnimation} 0.3s ease-in;
    `}
`;

export const StyledAvatar = styled(Avatar).attrs({
  squared: true,
  variant: AvatarVariant.MEDIUM,
})``;

export const UserName = styled.span<WithDrawerIsOpen>`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 20rem;

  display: ${(props: WithDrawerIsOpen) => (props.$drawerIsOpen ? 'block' : 'none')};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

export interface NavigationItemProps extends AppDrawerContentContainerProps {
  $isActive?: boolean;
}

export const AvatarContainer = styled(ContentContainer)<NavigationItemProps>`
  padding: 0.25rem;
  cursor: pointer;
  overflow: hidden;
  align-items: center;
  border-radius: 0.375rem;
  margin-top: 0.5rem;
  width: 100%;
  max-width: ${(props: WithDrawerIsOpen) => (props.$drawerIsOpen ? '100%' : '40px')};

  transition: padding ${drawerTransitionDuration};
  gap: 0.5rem;

  background: ${(props: NavigationItemProps & ThemeProp) =>
    props.$isActive ? `${props.theme.colors.main.c050} !important` : 'none'};

  &:hover {
    background: ${({ theme }: ThemeProp) => theme.colors.secondary['12-light-blue']};
  }

  ${UserName} {
    color: ${(props: NavigationItemProps & ThemeProp) =>
      props.$isActive ? props.theme.colors.main.c600 : props.theme.colors.content.default};
  }
`;

export const getIconStroke = ($isActive: boolean, $drawerIsOpen: boolean, theme: AppTheme) => {
  if (!$isActive && !$drawerIsOpen) {
    return theme.colors.content.default;
  }

  if (!$isActive && $drawerIsOpen) {
    return theme.colors.content.default;
  }

  return theme.colors.main.c600;
};

interface ItemListProp {
  $isActive?: boolean;
  $isOpened?: boolean;
}

export const ListingsItemTitle = styled.div<ItemListProp>`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.xxs};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${(props: ThemeProp & ItemListProp) =>
    props.$isActive ? props.theme.colors.primary.ca500 : props.theme.colors.greyscale.c050};
  ${(props: ThemeProp & ItemListProp) =>
    setSVGChildColors(getIconStroke(props.$isActive, props.$isOpened, props.theme))}
  padding-left: .5rem;
`;

export const ListingItem = styled.div<ItemListProp>`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-weight: ${(props: ThemeProp & ItemListProp) => props.theme.fontWeight.medium};
  line-height: 1.125rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${(props: ThemeProp & ItemListProp) =>
    props.$isActive ? props.theme.colors.primary.ca500 : props.theme.colors.greyscale.c050};
  padding: 0.5625rem 0.5rem;
  border-radius: 0.25rem;
  background: ${(props: ItemListProp & ThemeProp) =>
    props.$isActive ? `${props.theme.colors.main.c050} !important` : 'none'};
  &:hover {
    background: ${(props: ThemeProp) => `${props.theme.colors.main.c050} !important`};
  }
`;

export const ExpandedListingItemsContainer = styled.div<ItemListProp>`
  display: flex;
  flex-direction: column;
`;

interface MoreBubbleProps {
  $shouldDisplay?: boolean;
}

export const MoreBubble = styled.div<MoreBubbleProps>`
  position: absolute;
  bottom: 0.68rem;
  display: ${(props: MoreBubbleProps) => (props.$shouldDisplay ? 'flex' : 'none')};
  gap: 0.5rem;
  background: ${(props: ThemeProp) => props.theme.colors.primary.c300};
  border-radius: 1.875rem;
  justify-self: center;
  align-items: center;
  justify-content: center;
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c800};
  padding: 8px 16px 8px 12px;
  font-weight: 400;
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.lineHeight};
`;

export const StyledArrowDownIcon = styled(ArrowDownIcon)`
  stroke: ${(props: ThemeProp) => props.theme.colors.greyscale.c800};
`;

export const NavigationButtonContainer = styled.div<any>`
  height: ${(props) => (props.displayDropdown ? '0' : `${(navigationItemHeight + navigationItemMargin) / 16}rem`)};
  visibility: ${(props) => (props.displayDropdown ? 'hidden' : 'visible')};
`;

export const DropdownContainer = styled.div<any>`
  height: ${(props) => (props.isOpened ? 'auto' : `${(navigationItemHeight + navigationItemMargin) / 16}rem`)};
  position: relative;
`;

interface StyledCollapseProps {
  $isActive?: boolean;
  $isOpened?: boolean;
}

export const StyledCollapse = styled(Collapse).attrs((props: ThemeProp & StyledCollapseProps) => {
  const mainColor = `${props.theme.colors.main.c050}`;
  const mainColorAccent = `${props.theme.colors.main.c050}`;

  return {
    ...props,
    css: {
      background: props.$isActive ? mainColor : 'none',
      '&:hover': {
        background: props.$isActive ? `${mainColorAccent} !important` : `${mainColor} !important`,
      },
      '&.nextui-collapse--open': {
        background: `${mainColor} !important`,
      },
    },
  };
})`
  .nextui-collapse-title-content-right {
    svg {
      stroke: ${(props: ThemeProp & StyledCollapseProps) =>
        props.$isActive ? props.theme.colors.primary.ca500 : props.theme.colors.greyscale.c050};
    }
  }

  .nextui-collapse-content {
    padding-bottom: 0;
  }

  ${StyledCollapseTitle} {
    padding: 0.5rem 0 !important;
  }
`;

export const CollapseContainer = styled.div<ItemListProp>`
  margin-bottom: ${navigationItemMargin / 16}rem;
`;

/**
 * Notifications
 */

export interface NotificationsContainerProps {
  $display: boolean;
  $position: {
    left?: number;
  };
}

export const NotificationsContainer = styled.div<NotificationsContainerProps>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: ${(props: NotificationsContainerProps) => (props.$display ? `${props.$position.left}px` : '-999px')};
  background: ${(props: ThemeProp) => props.theme.colors.background};
  border-right: 1px solid ${(props: ThemeProp) => props.theme.colors.greyscale.c800};
  z-index: ${ZIndexLayers.fixedElement};
  width: 26.875rem;
  padding: 1.5rem 0 0 0;
  transition: left ${drawerTransitionDuration};
  display: flex;
  flex-direction: column;
`;

interface NotificationsHeaderProps {
  $showBottomBorder?: boolean;
}

export const NotificationsHeader = styled.div<NotificationsHeaderProps>`
  display: flex;
  padding: 0px 1rem 1.25rem 1rem;
  justify-content: space-between;
  border-bottom: 1px solid
    ${(props: NotificationsHeaderProps & ThemeProp) =>
      props.$showBottomBorder ? props.theme.colors.layout.divider : 'transparent'};
`;

export const NotificationsHeaderLeftContent = styled.div`
  display: flex;
  align-items: center;
`;

export const NotificationsHeaderRightContent = styled.div`
  display: flex;
  align-items: center;
`;

export const NotificationsHeaderTitle = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.l};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.l};
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
  margin-left: 0.5rem;
`;

export const NotificationsGoBackContainer = styled.div`
  cursor: pointer;
  display: flex;
`;

export const NotificationsExitIcon = styled(NotificationsCloseIcon)`
  stroke: ${(props: ThemeProp) => props.theme.colors.neutrals.c500};
`;

/**
 * We use this for animations. As the drawer has transparencies,
 * we need this overlay so elements can transition coming
 * from behind.
 */
export const AppDrawerContentOverlay = styled.div`
  position: absolute;
  width: 100%;
  top: 0;
  bottom: 0;
  background-color: ${(props: ThemeProp) => props.theme.colors.primary.c900};
  z-index: ${zIndexLayers.elevateOne};
`;

export const StyledPopoverContent = styled(Popover.Content)`
  border: solid 1px ${(props: ThemeProp) => props.theme.colors.greyscale.c800};
  border-radius: 0.25rem;
`;

export const LogoutModalContainer = styled.div`
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c100};
  text-align: left;
`;

interface BackButtonProps {
  $drawerIsOpen: boolean;
}

export const BackButton = styled.div<BackButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0.3rem;
  border-radius: ${(props: ThemeProp) => props.theme.borderRadius};
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c200};
  stroke: ${(props: ThemeProp) => props.theme.colors.content.subdued.high};

  :hover {
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
  }
`;

export const BackButtonContainer = styled.div<BackButtonProps>`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 1rem;
  padding: 0.5rem 0.5rem;
  transition: padding 300ms;
  white-space: nowrap;
  gap: 0.5rem;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c900};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};

  span {
    width: 100%;
    flex: 1;
  }

  :hover {
    ${BackButton} {
      background: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
    }
  }
`;
